import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useParams, Outlet, generatePath, useNavigate } from 'react-router-dom';

import { loadingSelector } from 'domain/env/envSelector';
import { isProvidersLoadedSelector } from 'domain/paySettings';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import * as ACL from 'domain/restriction';

import LayoutPayCompanySettings from 'components/mui/Layouts/PayCompanySettings';
import StickySubheader from 'components/mui/Layouts/components/StickySubheader';
import CircularProgressWithBackdrop from 'components/mui/CircularProgressWithBackdrop';
import Tabs from 'components/Tabs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

const Configurations: React.FC = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { companyId, '*': asterisk } = useParams();

  const loading = useSelector(loadingSelector);
  const isProvidersLoaded = useSelector(isProvidersLoadedSelector);
  const restrictions = useSelector(ACL.myRestriction);

  const [tab] = (asterisk ?? '').split('/');
  const ACLFilterFN = useMemo(() => ACL.arrayFilter(restrictions), [restrictions]);

  const tabs = useMemo(
    () => [
      {
        id: 'configurations.sidebar.paymentProviders',
        defaultMessage: 'Payment Providers',
        to: generatePath(ROUTES_PATH.PAY_COMPANY_SETTINGS_PROVIDERS.absolute, { companyId }),
        disabled: false,
        tabValue: 'providers',
      },
      {
        id: 'configurations.sidebar.beneficiaries',
        defaultMessage: 'Beneficiaries',
        to: generatePath(ROUTES_PATH.PAY_COMPANY_SETTINGS_BENEFICIARIES.absolute, { companyId }),
        disabled: !ACLFilterFN(ROUTES_PATH.PAY_COMPANY_SETTINGS_BENEFICIARIES),
        tabValue: 'beneficiaries',
      },
    ],
    [companyId, ACLFilterFN],
  );

  useEffect(() => {
    const defaultTabLink = tabs[0]?.to;
    if (!tab && defaultTabLink) {
      navigate(defaultTabLink);
    }
  }, [tab, navigate, tabs]);

  return (
    <LayoutPayCompanySettings>
      <CircularProgressWithBackdrop isOpen={loading} />

      <StickySubheader>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">
            {formatMessage({ id: 'configurations.page.title', defaultMessage: 'Settings' })}
          </Typography>
        </Box>
      </StickySubheader>
      <Container maxWidth={false}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} tabs={tabs} title={undefined} />
        </Box>
      </Container>

      <Container maxWidth={false} sx={{ flexGrow: 1, py: 2, display: 'flex', flexDirection: 'column' }}>
        {isProvidersLoaded && <Outlet />}
      </Container>
    </LayoutPayCompanySettings>
  );
};

export default Configurations;
